import { ITripGroup } from '../types/trip.types';
import { ILineItem } from '../types/line-item.types';
import { ExpenseType } from '../constants/expense-types';

export interface ApprovalDisableState {
    hasReviewRequiredLineItems: boolean;
    hasMileageReviewRequired: boolean;
    hasPendingLineItemChanges: boolean;
    /** True when pending changes would block approval (e.g. user set review required to true). When only change is clearing review required (false), approval is allowed. */
    hasPendingChangesThatBlockApproval?: boolean;
    hasDeclinedTrips?: boolean;
    isProcessing?: boolean;
}

export const checkTripApprovalDisabled = (
    tripGroup: ITripGroup,
    reviewRequiredState: Map<number, boolean>
): ApprovalDisableState => {
    const hasReviewRequiredLineItems = tripGroup.lineItems.some(li => {
        const stateValue = reviewRequiredState.get(li.id);
        return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
    });

    const hasMileageReviewRequired = tripGroup.lineItems.some(li => {
        if (li.expenseType === ExpenseType.MILEAGE) {
            const stateValue = reviewRequiredState.get(li.id);
            return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
        }
        return false;
    });

    const hasPendingLineItemChanges = tripGroup.status !== 'pending' && tripGroup.lineItems.some(li => {
        const stateValue = reviewRequiredState.get(li.id);
        return stateValue !== undefined && stateValue !== li.reviewRequired;
    });

    // Only block approval when pending change is "user set review required to true" (flagged for review, not saved).
    // When user unchecked review required (state false, server true), allow approval — payload will send the update.
    const hasPendingChangesThatBlockApproval = tripGroup.status !== 'pending' && tripGroup.lineItems.some(li => {
        const stateValue = reviewRequiredState.get(li.id);
        return stateValue === true && li.reviewRequired === false;
    });

    return {
        hasReviewRequiredLineItems,
        hasMileageReviewRequired,
        hasPendingLineItemChanges,
        hasPendingChangesThatBlockApproval
    };
};

export const checkClaimApprovalDisabled = (
    tripGroups: ITripGroup[],
    allLineItems: ILineItem[],
    reviewRequiredState: Map<number, boolean>
): ApprovalDisableState => {
    const hasDeclinedTrips = tripGroups.some(tg => tg.status === 'declined');

    const hasReviewRequiredLineItems = allLineItems.some(li => {
        const stateValue = reviewRequiredState.get(li.id);
        return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
    });

    const allTripsMileageReviewRequired = allLineItems.some(li => {
        if (li.expenseType === ExpenseType.MILEAGE) {
            const stateValue = reviewRequiredState.get(li.id);
            return stateValue !== undefined ? stateValue === true : li.reviewRequired === true;
        }
        return false;
    });

    const hasPendingLineItemChanges = tripGroups.some(tg => {
        if (tg.status === 'pending') return false;
        return tg.lineItems.some(li => {
            const stateValue = reviewRequiredState.get(li.id);
            return stateValue !== undefined && stateValue !== li.reviewRequired;
        });
    });

    // Only block approval when pending change is "user set review required to true" (flagged for review, not saved).
    const hasPendingChangesThatBlockApproval = tripGroups.some(tg => {
        if (tg.status === 'pending') return false;
        return tg.lineItems.some(li => {
            const stateValue = reviewRequiredState.get(li.id);
            return stateValue === true && li.reviewRequired === false;
        });
    });

    return {
        hasReviewRequiredLineItems,
        hasMileageReviewRequired: allTripsMileageReviewRequired,
        hasPendingLineItemChanges,
        hasPendingChangesThatBlockApproval,
        hasDeclinedTrips
    };
};

export const isApproveDisabled = (state: ApprovalDisableState): boolean => {
    const pendingBlocksApproval = state.hasPendingChangesThatBlockApproval ?? state.hasPendingLineItemChanges;
    return state.hasReviewRequiredLineItems ||
           state.hasMileageReviewRequired ||
           pendingBlocksApproval ||
           (state.hasDeclinedTrips ?? false) ||
           (state.isProcessing ?? false);
};

export const isDeclineDisabled = (state: ApprovalDisableState): boolean => {
    return state.isProcessing ?? false;
};

